import React, { useState, useEffect } from 'react'
import { BREAKPOINTS, DEFAULT_BREAKPOINTS_CONFIG } from './constant'

export type BreakpointType = typeof BREAKPOINTS
type BreakpointTypeKeys = keyof BreakpointType
export type Breakpoints = BreakpointType[BreakpointTypeKeys]

export interface BREAKPOINTS_MAP {
  Small: number
  Medium: number
  Large: number
}

interface QueryMatches {
  [key: string]: boolean
}

interface BreakpointResponse {
  screenSize: Breakpoints | undefined
  screenIs: (breakpoint?: Breakpoints) => boolean | string | undefined
  config: BREAKPOINTS_MAP
  setConfig: React.Dispatch<React.SetStateAction<BREAKPOINTS_MAP>>
}

export const useBreakpoint = (breakpointsConfig?: BREAKPOINTS_MAP): BreakpointResponse => {
  const breakpoints = Object.values(BREAKPOINTS)
  const [config, setConfig] = useState<BREAKPOINTS_MAP>(breakpointsConfig ?? DEFAULT_BREAKPOINTS_CONFIG)
  const [screenSize, setScreenSize] = useState<Breakpoints>()
  const browserSupportEventTarget = typeof window !== 'undefined' && typeof window.matchMedia('(min-width: 320px)').addEventListener !== 'undefined'

  const updateMatchesAndScreenSize = (mediaQueryLists: MediaQueryList[]): void => {
    const queryMatches = breakpoints.reduce((acc: QueryMatches, breakpoint, index) => {
      acc[breakpoint] = mediaQueryLists[index]?.matches
      return acc
    }, {})
    const size = breakpoints.filter((breakpoint) => queryMatches[breakpoint]).pop() ?? 'Small'
    setScreenSize(size)
  }

  useEffect(() => {
    const mediaQueryLists = breakpoints.map((breakpoint) => {
      const query = `(min-width: ${config[breakpoint]}px)`
      return window.matchMedia(query)
    })

    updateMatchesAndScreenSize(mediaQueryLists)

    const handleQueryListener = (): void => updateMatchesAndScreenSize(mediaQueryLists)

    if (browserSupportEventTarget) {
      mediaQueryLists.map((mediaQueryList) => mediaQueryList.addEventListener('change', handleQueryListener))
    } else {
      // Deprecated 'MediaQueryList' API, <Safari 14, IE, <Edge 16
      mediaQueryLists.map((mediaQueryList) => mediaQueryList.addListener(handleQueryListener))
    }

    return () => {
      if (browserSupportEventTarget) {
        mediaQueryLists.map((mediaQueryList) => mediaQueryList.removeEventListener('change', handleQueryListener))
      } else {
        // Deprecated 'MediaQueryList' API, <Safari 14, IE, <Edge 16
        mediaQueryLists.map((mediaQueryList) => mediaQueryList.removeListener(handleQueryListener))
      }
    }
  }, [config])

  const screenIs = (breakpoint?: Breakpoints): boolean | string | undefined => {
    if (typeof breakpoint === 'undefined') {
      return screenSize
    }
    return screenSize === breakpoint
  }

  return { screenSize, screenIs, config, setConfig }
}
